import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsBadgeBitComponent } from '../../bits/cads-badge-bit/cads-badge-bit.component';
import type { CadsBadgeBitConfig } from '../../bits/cads-badge-bit/cads-badge-bit.config';
import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';

import { CadsListItemConfig } from './cads-list-item.config';

/**
 * Instructional **row**: leading {@link CadsIconViewBitComponent} (sprite icon tile or step number in a circle) plus title,
 * optional {@link CadsBadgeBitComponent}, optional plain-text {@link CadsListItemTrailing}, and description. No row chrome or outer inset—place inside
 * {@link CadsCardViewComponent} (which pads the slot) or add host padding when using a plain stack. Consecutive
 * sibling instances get vertical spacing via inner `padding-block-start` (non-first) and `padding-block-end`
 * (non-last) so host-level borders (e.g. `border-top` on `<cads-list-item>`) sit flush under the block above without a
 * margin gap above the rule, while leaving space after each row’s content. See
 * {@link CadsListItemConfig.tinted} for title, description, and leading glyph emphasis on colored card surfaces.
 */
@Component({
    selector: 'cads-list-item',
    imports: [CadsBadgeBitComponent, CadsIconViewBitComponent],
    templateUrl: './cads-list-item.component.html',
    styleUrl: './cads-list-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      class: 'cads-list-item-host',
      '[class.cads-list-item-host--single-line]': '_singleLine()',
    },
})
export class CadsListItemComponent {
  readonly config = input.required<CadsListItemConfig>();

  /** Title-only rows (no description): center icon + title vertically in the card slot. */
  protected readonly _singleLine = computed(() => !this.config().description?.trim());

  protected readonly _leadingIconConfig = computed((): CadsIconViewBitConfig => {
    const c = this.config();
    const theme = `var(--cads-color-${c.color})`;
    const deepTheme = `var(--cads-color-${c.color}-deep)`;
    const lead = c.leading;
    if (lead.kind === 'step') {
      return {
        label: String(lead.step),
        shape: 'circle',
        size: 'medium',
        color: theme,
        iconColor: c.tinted ? deepTheme : undefined,
      };
    }
    return {
      icon: lead.icon,
      shape: lead.shape ?? 'rounded',
      size: 'medium',
      color: theme,
      iconColor: c.tinted ? deepTheme : undefined,
    };
  });

  protected readonly _badgeConfig = computed((): CadsBadgeBitConfig | null => {
    const b = this.config().badge;
    if (!b) {
      return null;
    }
    const label = b.label?.trim();
    if (!label) {
      return null;
    }
    return { ...b, label, size: b.size ?? 'xs' };
  });

  protected readonly _trailingLabel = computed(() => {
    const label = this.config().trailing?.label?.trim();
    return label || null;
  });

  protected readonly _trailingColor = computed(() => {
    const color = this.config().trailing?.color ?? 'mid-gray';
    return `var(--cads-color-${color}-deep)`;
  });

  protected readonly _descriptionColor = computed(() => {
    const c = this.config();
    if (!c.tinted) {
      return 'var(--cads-color-text-muted)';
    }
    return `var(--cads-color-${c.color}-deep)`;
  });

  /** Title-only rows use the base semantic color; tinted rows use **-deep** to match colored card surfaces. */
  protected readonly _titleColor = computed(() => {
    const c = this.config();
    if (this._singleLine()) {
      return `var(--cads-color-${c.color})`;
    }
    if (c.tinted) {
      return `var(--cads-color-${c.color}-deep)`;
    }
    return 'var(--cads-color-text)';
  });
}
